import React, { useState, useEffect, useRef } from 'react';

interface TaxCalculatorResult {
  netAmount: string;
  taxPercent: string;
  grossAmount: string;
}

interface CalculatorProps {
  isOpen: boolean;
  onClose: () => void;
  onResult: (result: string) => void;
  onTaxResult?: (result: TaxCalculatorResult) => void;
  position: { x: number; y: number };
}

const Calculator: React.FC<CalculatorProps> = ({ isOpen, onClose, onResult, onTaxResult, position }) => {
  const [activeTab, setActiveTab] = useState<'calculator' | 'tax'>('calculator');
  
  // Calculator tab state
  const [formula, setFormula] = useState('');
  const [result, setResult] = useState('');
  const formulaInputRef = useRef<HTMLInputElement>(null);
  
  // Tax calculator tab state
  const [grossAmount, setGrossAmount] = useState('');
  const [taxPercent, setTaxPercent] = useState('');
  const [netAmount, setNetAmount] = useState('');
  const grossInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      if (activeTab === 'calculator' && formulaInputRef.current) {
        formulaInputRef.current.focus();
      } else if (activeTab === 'tax' && grossInputRef.current) {
        grossInputRef.current.focus();
      }
    }
  }, [isOpen, activeTab]);

  // Calculator formula evaluation
  useEffect(() => {
    if (formula) {
      try {
        // Safe evaluation of mathematical expressions
        const sanitizedFormula = formula.replace(/[^0-9+\-*/().]/g, '');
        if (sanitizedFormula) {
          const evalResult = Function('"use strict"; return (' + sanitizedFormula + ')')();
          if (typeof evalResult === 'number' && !isNaN(evalResult)) {
            // Format the result with commas for thousands separators
            const formattedResult = Math.abs(evalResult).toLocaleString('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 10
            });
            setResult(formattedResult);
          } else {
            setResult('');
          }
        } else {
          setResult('');
        }
      } catch (error) {
        setResult('');
      }
    } else {
      setResult('');
    }
  }, [formula]);

  // Tax calculation
  useEffect(() => {
    if (grossAmount && taxPercent) {
      try {
        const gross = parseFloat(grossAmount.replace(/,/g, '')) || 0;
        const tax = parseFloat(taxPercent) || 0;
        
        if (gross > 0 && tax >= 0 && tax <= 100) {
          const net = gross / (1 + (tax / 100));
          const formattedNet = Math.round(net).toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          });
          setNetAmount(formattedNet);
        } else {
          setNetAmount('');
        }
      } catch (error) {
        setNetAmount('');
      }
    } else {
      setNetAmount('');
    }
  }, [grossAmount, taxPercent]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleOK();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
    }
  };

  const handleOK = () => {
    if (activeTab === 'calculator' && result) {
      // Remove commas from the formatted result before passing it back
      const numericResult = result.replace(/,/g, '');
      onResult(numericResult);
    } else if (activeTab === 'tax' && netAmount && onTaxResult) {
      // Pass tax calculation results
      onTaxResult({
        netAmount: netAmount.replace(/,/g, ''),
        taxPercent: taxPercent,
        grossAmount: grossAmount.replace(/,/g, '')
      });
    }
    handleClose();
  };

  const handleClear = () => {
    if (activeTab === 'calculator') {
      setFormula('');
      setResult('');
      if (formulaInputRef.current) {
        formulaInputRef.current.focus();
      }
    } else {
      setGrossAmount('');
      setTaxPercent('');
      setNetAmount('');
      if (grossInputRef.current) {
        grossInputRef.current.focus();
      }
    }
  };

  const handleClose = () => {
    // Clear all state
    setFormula('');
    setResult('');
    setGrossAmount('');
    setTaxPercent('');
    setNetAmount('');
    setActiveTab('calculator');
    onClose();
  };

  const handleTabChange = (tab: 'calculator' | 'tax') => {
    setActiveTab(tab);
  };

  const handleGrossAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty or numeric input with commas
    if (value === '' || /^[\d,]+$/.test(value)) {
      setGrossAmount(value);
    }
  };

  const handleTaxPercentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty or numeric input (including decimals)
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setTaxPercent(value);
    }
  };

  const formatGrossAmountOnBlur = () => {
    if (grossAmount) {
      const numValue = parseFloat(grossAmount.replace(/,/g, '')) || 0;
      const formatted = numValue.toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      });
      setGrossAmount(formatted);
    }
  };

  if (!isOpen) return null;

  const hasValidResult = activeTab === 'calculator' ? !!result : !!netAmount;

  return (
    <div 
      className="calculator-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={handleClose}
    >
      <div 
        className="calculator-popup"
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          padding: '20px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          minWidth: '350px',
          maxWidth: '450px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Tab Header */}
        <div className="calculator-tabs" style={{ marginBottom: '15px' }}>
          <div style={{ display: 'flex', borderBottom: '1px solid #ddd' }}>
            <button
              onClick={() => handleTabChange('calculator')}
              style={{
                flex: 1,
                padding: '10px 16px',
                border: 'none',
                backgroundColor: activeTab === 'calculator' ? '#007bff' : 'transparent',
                color: activeTab === 'calculator' ? 'white' : '#666',
                cursor: 'pointer',
                borderRadius: '4px 4px 0 0',
                fontWeight: activeTab === 'calculator' ? '600' : '400',
                fontSize: '14px'
              }}
            >
              Calculator
            </button>
            <button
              onClick={() => handleTabChange('tax')}
              style={{
                flex: 1,
                padding: '10px 16px',
                border: 'none',
                backgroundColor: activeTab === 'tax' ? '#007bff' : 'transparent',
                color: activeTab === 'tax' ? 'white' : '#666',
                cursor: 'pointer',
                borderRadius: '4px 4px 0 0',
                fontWeight: activeTab === 'tax' ? '600' : '400',
                fontSize: '14px'
              }}
            >
              Tax Calculator
            </button>
          </div>
        </div>

        {/* Calculator Tab */}
        {activeTab === 'calculator' && (
          <div className="calculator-body">
            <div className="calculator-input-group" style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Formula:</label>
              <input
                ref={formulaInputRef}
                type="text"
                value={formula}
                onChange={(e) => setFormula(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="e.g., 52032*253652"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div className="calculator-result-group" style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Result:</label>
              <div 
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#f8f9fa',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  height: '38px',
                  display: 'flex',
                  alignItems: 'center',
                  boxSizing: 'border-box'
                }}
              >
                {result}
              </div>
            </div>
          </div>
        )}

        {/* Tax Calculator Tab */}
        {activeTab === 'tax' && (
          <div className="tax-calculator-body">
            <div className="tax-input-group" style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Gross Amount (VND):</label>
              <input
                ref={grossInputRef}
                type="text"
                value={grossAmount}
                onChange={handleGrossAmountChange}
                onBlur={formatGrossAmountOnBlur}
                onKeyDown={handleKeyDown}
                placeholder="e.g., 1,000,000"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div className="tax-input-group" style={{ marginBottom: '10px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Tax Percent:</label>
              <input
                type="text"
                value={taxPercent}
                onChange={handleTaxPercentChange}
                onKeyDown={handleKeyDown}
                placeholder="e.g., 5, 10"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              />
            </div>
            
            <div className="tax-result-group" style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Net Amount (VND):</label>
              <div 
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: '#f8f9fa',
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  height: '38px',
                  display: 'flex',
                  alignItems: 'center',
                  boxSizing: 'border-box',
                  color: netAmount ? '#000' : '#999'
                }}
              >
                {netAmount || 'Enter gross amount and tax %'}
              </div>
            </div>
          </div>
        )}
        
        <div className="calculator-footer">
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={handleClear}
              style={{
                padding: '8px 16px',
                border: '1px solid #6c757d',
                borderRadius: '4px',
                backgroundColor: '#6c757d',
                color: 'white',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Clear
            </button>
            <button
              onClick={handleClose}
              style={{
                padding: '8px 16px',
                border: '1px solid #dc3545',
                borderRadius: '4px',
                backgroundColor: '#dc3545',
                color: 'white',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Close
            </button>
            <button
              onClick={handleOK}
              disabled={!hasValidResult}
              style={{
                padding: '8px 16px',
                border: '1px solid #007bff',
                borderRadius: '4px',
                backgroundColor: hasValidResult ? '#007bff' : '#ccc',
                color: 'white',
                cursor: hasValidResult ? 'pointer' : 'not-allowed',
                fontWeight: '500'
              }}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calculator; 